/**
 * Prufe Auth Provider
 *
 * Manages authentication state via Cognito.
 * On mount: checks for existing session, auto-refreshes tokens.
 * Provides auth context to the entire app.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import * as cognito from './cognitoService';
import { useAuthStore } from '@/shared/state/authStore';
import { jmapClient } from '@/shared/api/jmapClient';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { email: string; tenantId: string | null; accountId: string | null; role: string | null } | null;
  cognitoUser: CognitoUser | null;
  signIn: (email: string, password: string) => Promise<cognito.SignInResult | cognito.MfaChallengeResult>;
  handleMfaChallenge: (code: string, cognitoUser: CognitoUser) => Promise<CognitoUserSession>;
  signUp: (email: string, password: string, tenantName: string) => ReturnType<typeof cognito.signUp>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => void;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  setupMfa: (user: CognitoUser) => Promise<string>;
  verifyMfaSetup: (code: string, user: CognitoUser) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Refresh tokens 5 minutes before expiry
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthContextValue['user']>(null);
  const [currentCognitoUser, setCurrentCognitoUser] = useState<CognitoUser | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const store = useAuthStore;

  const applySession = useCallback((session: CognitoUserSession) => {
    const idToken = session.getIdToken().getJwtToken();
    const accessToken = session.getAccessToken().getJwtToken();
    const refreshToken = session.getRefreshToken().getToken();
    const parsed = cognito.parseIdToken(idToken);

    // Update zustand store
    store.getState().setTokens(accessToken, idToken, refreshToken);
    store.setState({
      isAuthenticated: true,
      email: parsed.email,
      tenantId: parsed.tenantId,
      accountId: parsed.accountId,
      role: (parsed.role as 'owner' | 'admin' | 'user') ?? null,
      tokenExpiresAt: parsed.exp * 1000,
    });

    // Wire jmapClient
    jmapClient.setAccessToken(idToken);
    if (parsed.tenantId && parsed.accountId) {
      jmapClient.setContext(parsed.tenantId, parsed.accountId);
    }

    setUser({
      email: parsed.email,
      tenantId: parsed.tenantId,
      accountId: parsed.accountId,
      role: parsed.role,
    });
    setIsAuthenticated(true);

    // Schedule token refresh
    scheduleRefresh(parsed.exp * 1000);
  }, [store]);

  const scheduleRefresh = useCallback((expiresAtMs: number) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const delay = Math.max(expiresAtMs - Date.now() - REFRESH_BUFFER_MS, 0);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const session = await cognito.refreshSession();
        applySession(session);
      } catch {
        // Refresh failed — user needs to re-authenticate
        handleSignOut();
      }
    }, delay);
  }, [applySession]);

  // Check for existing session on mount
  useEffect(() => {
    cognito.configure();
    let cancelled = false;

    (async () => {
      try {
        const session = await cognito.getSession();
        if (!cancelled) applySession(session);
        const cogUser = cognito.getCurrentUser();
        if (!cancelled && cogUser) setCurrentCognitoUser(cogUser);
      } catch {
        // No valid session — user is not authenticated
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [applySession]);

  const handleSignIn = useCallback(async (email: string, password: string) => {
    const result = await cognito.signIn(email, password);
    if ('session' in result) {
      applySession(result.session);
      setCurrentCognitoUser(result.cognitoUser);
    } else {
      setCurrentCognitoUser(result.cognitoUser);
    }
    return result;
  }, [applySession]);

  const handleMfa = useCallback(async (code: string, cognitoUser: CognitoUser) => {
    const session = await cognito.handleMfaChallenge(code, cognitoUser);
    applySession(session);
    return session;
  }, [applySession]);

  const handleSignOut = useCallback(() => {
    cognito.signOut();
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    store.getState().logout();
    setIsAuthenticated(false);
    setUser(null);
    setCurrentCognitoUser(null);
  }, [store]);

  const value: AuthContextValue = {
    isAuthenticated,
    isLoading,
    user,
    cognitoUser: currentCognitoUser,
    signIn: handleSignIn,
    handleMfaChallenge: handleMfa,
    signUp: cognito.signUp,
    confirmSignUp: cognito.confirmSignUp,
    signOut: handleSignOut,
    forgotPassword: cognito.forgotPassword,
    confirmForgotPassword: cognito.confirmForgotPassword,
    setupMfa: cognito.setupMfa,
    verifyMfaSetup: cognito.verifyMfaSetup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
